import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { CommonButton } from "~/components/ui/commonButton";
import KnowledgeInfoTooltip from "~/components/ui/knowledgeInfoTooltip";
import Modal from "~/components/ui/modal";
import { MAX_ACTIVE_KNOWLEDGE_PER_TYPE } from "~/constants/knowledge";
import { TOAST_MESSAGES } from "~/constants/messages";
import { KnowledgeApi } from "~/functions/api/knowledge";
import { refetchAllPendingKnowledgeCounts } from "~/hooks/usePendingKnowledgeCount";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import AddWhiteIcon from "~/images/add_white_icon.svg?react";
import EmptyStateIcon from "~/images/Group26781.svg?react";
import type { Knowledge } from "~/interface/knowledge";
import type { RootState } from "~/store";
import CreateKnowledgeModal, { type CreateKnowledgeModalRef } from "./CreateKnowledgeModal";
import styles from "./index.module.scss";
import KnowledgeTable from "./KnowledgeTable";
import PendingKnowledgeModal from "./PendingKnowledgeModal";
import pendingStyles from "./PendingKnowledgeModal.module.scss";

// ドキュメントタイプの定義
const DOCUMENT_TYPES = [
  { key: "system-proposal", label: "システム概要書" },
  { key: "functional-requirements", label: "機能要件書" },
  { key: "non-functional-requirements", label: "非機能要件書" },
  { key: "plugin-proposal", label: "プラグイン提案書" },
  { key: "app-design", label: "アプリ設計書" },
  { key: "field-design", label: "フィールド設計書" },
  { key: "customize-specification", label: "カスタマイズ仕様書" },
] as const;

type DocumentType = (typeof DOCUMENT_TYPES)[number]["key"];

const Knowledges: React.FC = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isPendingModalOpen, setPendingModalOpen] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState<Knowledge | null>(null);
  const [knowledges, setKnowledges] = useState<Knowledge[]>([]);
  const [allKnowledges, setAllKnowledges] = useState<Knowledge[]>([]); // 全データを保持
  const [pendingKnowledges, setPendingKnowledges] = useState<Knowledge[]>([]); // 承認待ちデータ
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<DocumentType>("system-proposal"); // 選択されたタイプ
  const [searchQuery, setSearchQuery] = useState<string>(""); // 検索クエリ

  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth) as any;
  const { canManageKnowledge, canViewKnowledge } = useProjectPermission();
  const knowledgeApi = useMemo(() => new KnowledgeApi(), []);

  // Create/Edit modal footer control refs and states
  const createModalRef = React.useRef<CreateKnowledgeModalRef>(null);
  const editModalRef = React.useRef<CreateKnowledgeModalRef>(null);
  const [createFooterState, setCreateFooterState] = useState<{
    canSubmit: boolean;
    isSubmitting: boolean;
  }>({
    canSubmit: false,
    isSubmitting: false,
  });
  const [editFooterState, setEditFooterState] = useState<{
    canSubmit: boolean;
    isSubmitting: boolean;
  }>({
    canSubmit: false,
    isSubmitting: false,
  });

  // APIからナレッジデータを取得
  const fetchKnowledges = useCallback(async () => {
    if (!currentProject?.id || !currentUser?.organization_id) return;

    setIsLoading(true);
    try {
      // 承認済みと承認待ちのナレッジを並行取得
      const [approvedData, pendingData] = await Promise.all([
        knowledgeApi.getByStatus(currentProject.id, currentUser.organization_id, "approved"),
        knowledgeApi.getByStatus(currentProject.id, currentUser.organization_id, "pending"),
      ]);

      // データを配列に変換
      const approvedArray = approvedData
        ? Array.isArray(approvedData)
          ? approvedData
          : [approvedData]
        : [];
      const pendingArray = pendingData
        ? Array.isArray(pendingData)
          ? pendingData
          : [pendingData]
        : [];

      setAllKnowledges(approvedArray); // 承認済みデータを保存
      setPendingKnowledges(pendingArray); // 承認待ちデータを保存
    } catch (error) {
      console.error("Failed to fetch knowledges:", error);
      setAllKnowledges([]);
      setPendingKnowledges([]);
      setKnowledges([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentProject?.id, currentUser?.organization_id, knowledgeApi]);

  // タイプごとにナレッジをフィルタリング（検索クエリも適用）
  const filterKnowledgesByType = (data: Knowledge[], type: DocumentType, query: string = "") => {
    let filtered = data.filter((item) => item.type === type);

    // 検索クエリがある場合、タイトルとプロンプトで絞り込み
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(lowerQuery) ||
          item.meta_prompt?.toLowerCase().includes(lowerQuery),
      );
    }

    setKnowledges(filtered);
  };

  // ドキュメントタイプが選択された時の処理
  const handleTypeSelect = (type: DocumentType) => {
    setSelectedType(type);
    setSearchQuery(""); // タイプ変更時は検索クエリをクリア
    filterKnowledgesByType(allKnowledges, type, "");
  };

  // 検索クエリが変更された時の処理
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    filterKnowledgesByType(allKnowledges, selectedType, query);
  };

  useEffect(() => {
    if (currentProject?.id && currentUser?.organization_id) {
      fetchKnowledges();
    }
  }, [currentProject?.id, currentUser?.organization_id, fetchKnowledges]);

  // 開発用の強制モーダル表示は削除済み

  // selectedTypeまたはallKnowledgesが変更された時にフィルタリングを実行
  useEffect(() => {
    if (allKnowledges.length > 0) {
      let filtered = allKnowledges.filter((item) => item.type === selectedType);

      if (searchQuery.trim()) {
        const lowerQuery = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.title?.toLowerCase().includes(lowerQuery) ||
            item.meta_prompt?.toLowerCase().includes(lowerQuery),
        );
      }

      setKnowledges(filtered);
    }
  }, [selectedType, allKnowledges, searchQuery]);

  const openModal = () => {
    setModalOpen(true);
  };

  // モーダルを閉じる関数
  const closeModal = () => {
    setModalOpen(false);
  };

  // 編集モーダルを開く関数
  const openEditModal = (knowledge: Knowledge) => {
    setEditingKnowledge(knowledge);
    setEditModalOpen(true);
  };

  // 編集モーダルを閉じる関数
  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingKnowledge(null);
  };

  // ナレッジの編集
  const handleEdit = (knowledge: Knowledge) => {
    openEditModal(knowledge);
  };

  // ナレッジ作成後にリストを更新
  const handleKnowledgeCreated = (newKnowledge: Knowledge) => {
    const updatedAll = [...allKnowledges, newKnowledge];
    setAllKnowledges(updatedAll);

    // 新規作成されたナレッジのタイプが現在選択中のタイプと一致する場合のみ表示に追加
    if (newKnowledge.type === selectedType) {
      setKnowledges((prev) => [...prev, newKnowledge]);
    }
    closeModal();
  };

  // ナレッジ更新後にリストを更新
  const handleKnowledgeUpdated = (updatedKnowledge: Knowledge) => {
    // 全データを更新
    setAllKnowledges((prev) =>
      prev.map((item) => (item.id === updatedKnowledge.id ? updatedKnowledge : item)),
    );

    // 表示データを更新（現在のタイプと一致する場合のみ）
    if (updatedKnowledge.type === selectedType) {
      setKnowledges((prev) =>
        prev.map((item) => (item.id === updatedKnowledge.id ? updatedKnowledge : item)),
      );
    } else {
      // タイプが変更された場合は現在の表示から削除
      setKnowledges((prev) => prev.filter((item) => item.id !== updatedKnowledge.id));
    }

    closeEditModal();
  };

  // ナレッジの有効/無効状態を更新
  const handleToggleActive = async (knowledge: Knowledge) => {
    if (!currentUser?.id || !currentUser?.organization_id || !currentProject?.id) return;

    const newStatus = !knowledge.isActive;

    // 有効化しようとする場合、同じタイプの有効化数をチェック
    if (newStatus === true) {
      const activeCount = allKnowledges.filter(
        (k) => k.type === knowledge.type && k.isActive && k.id !== knowledge.id,
      ).length;

      if (activeCount >= MAX_ACTIVE_KNOWLEDGE_PER_TYPE) {
        toast.error(
          `このドキュメントタイプの有効化数は最大${MAX_ACTIVE_KNOWLEDGE_PER_TYPE}件までです。`,
        );
        return;
      }
    }

    try {
      const result = await knowledgeApi.updateIsActive(
        currentUser.organization_id,
        currentProject.id,
        knowledge.id,
        !knowledge.isActive,
      );

      // APIが成功した場合（trueが返された場合）
      if (result === true) {
        // ローカル状態を更新（予定していた新しい状態を使用）
        const updatedItem = { ...knowledge, isActive: newStatus };

        // 全データを更新
        setAllKnowledges((prev) =>
          prev.map((item) => (item.id === knowledge.id ? updatedItem : item)),
        );

        // 表示データを更新
        setKnowledges((prev) =>
          prev.map((item) => (item.id === knowledge.id ? updatedItem : item)),
        );

        // 成功時のtoast通知
        const statusText = newStatus ? "有効" : "無効";
        toast.success(TOAST_MESSAGES.KNOWLEDGE.STATUS_CHANGED(statusText));
      } else {
        toast.error(TOAST_MESSAGES.KNOWLEDGE.STATUS_UPDATE_FAILED);
      }
    } catch (error) {
      console.error("Failed to update knowledge status:", error);
      toast.error(TOAST_MESSAGES.KNOWLEDGE.STATUS_UPDATE_ERROR);
    }
  };

  // ナレッジの削除
  const handleDelete = async (knowledge: Knowledge) => {
    if (!currentUser?.id || !currentUser?.organization_id || !currentProject?.id) return;

    // 確認ダイアログを表示
    const confirmDelete = window.confirm(`「${knowledge.title}」を削除してもよろしいですか？`);
    if (!confirmDelete) {
      return;
    }

    try {
      const result = await knowledgeApi.delete(
        currentUser.organization_id,
        currentProject.id,
        knowledge.id,
      );

      if (result) {
        // 全データから削除
        setAllKnowledges((prev) => prev.filter((item) => item.id !== knowledge.id));

        // 表示データから削除
        setKnowledges((prev) => prev.filter((item) => item.id !== knowledge.id));

        toast.success(TOAST_MESSAGES.KNOWLEDGE.DELETE_SUCCESS);
      } else {
        toast.error(TOAST_MESSAGES.KNOWLEDGE.DELETE_FAILED);
      }
    } catch (error) {
      console.error("Failed to delete knowledge:", error);
      toast.error(TOAST_MESSAGES.KNOWLEDGE.DELETE_ERROR);
    }
  };

  // 承認待ちナレッジの確認処理
  const handlePendingConfirmation = () => {
    setPendingModalOpen(true);
  };

  // 承認待ちナレッジの承認処理
  const handleApprovePendingKnowledge = async (knowledge: Knowledge) => {
    if (!currentUser?.id || !currentUser?.organization_id || !currentProject?.id) {
      throw new Error("必要なパラメーターが不足しています");
    }

    const result = await knowledgeApi.approve(
      currentUser.organization_id,
      currentProject.id,
      knowledge.id,
      knowledge,
    );

    // APIが成功した場合（trueが返された場合）
    if (result === true) {
      // 承認済みリストに追加（pendingKnowledgesからはまだ削除しない）
      const approvedKnowledge = { ...knowledge, isApproved: true };
      setAllKnowledges((prev) => [...prev, approvedKnowledge]);

      // 現在選択中のタイプと一致する場合は表示リストにも追加
      if (knowledge.type === selectedType) {
        setKnowledges((prev) => [...prev, approvedKnowledge]);
      }

      // pending knowledgeカウントを更新
      refetchAllPendingKnowledgeCounts();
    } else {
      throw new Error("承認に失敗しました");
    }
  };

  // 承認待ちナレッジの破棄処理
  const handleDiscardPendingKnowledge = async (knowledge: Knowledge) => {
    if (!currentUser?.id || !currentUser?.organization_id || !currentProject?.id) {
      throw new Error("必要なパラメーターが不足しています");
    }

    const result = await knowledgeApi.delete(
      currentUser.organization_id,
      currentProject.id,
      knowledge.id,
    );

    if (result) {
      // 承認待ちリストから削除
      setPendingKnowledges((prev) => prev.filter((item) => item.id !== knowledge.id));

      // pending knowledgeカウントを更新
      refetchAllPendingKnowledgeCounts();
    } else {
      throw new Error("破棄に失敗しました");
    }
  };

  // 承認待ちナレッジ通知バナーコンポーネント
  const PendingNotificationBanner = () => {
    if (pendingKnowledges.length === 0) return null;

    return (
      <div className={styles["pendingBanner"]}>
        <div className={styles["pendingBannerContent"]}>
          <div className={styles["pendingBannerIcon"]}>i</div>
          <div className={styles["pendingBannerText"]}>
            ナレッジ候補が
            <span className={styles["pendingCount"]}>{pendingKnowledges.length}件</span>
            生成されました。必要なものは保存しましょう。
          </div>
          <Button styleType="white-middle" label={"確認"} onClick={handlePendingConfirmation} />
        </div>
      </div>
    );
  };

  // 現在選択されているタイプのラベルを取得
  const selectedTypeLabel =
    DOCUMENT_TYPES.find((type) => type.key === selectedType)?.label || selectedType;

  // 権限チェック: guestは表示不可
  if (!canViewKnowledge()) {
    return (
      <div className={styles["container"]}>
        <div className={styles["wrapper"]}>
          <div className={styles["content"]}>
            <div className={styles["emptyState"]}>
              <EmptyStateIcon />
              <p>ナレッジを表示する権限がありません。</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["container"]} data-testid="knowledge-page">
      <div className={styles["titleWithTooltip"]}>
        <h2 className={styles["title"]} data-testid="knowledge-heading">
          ナレッジ
        </h2>
        <KnowledgeInfoTooltip />
      </div>
      <div className={styles["wrapper"]}>
        <ul className={styles["list"]}>
          {DOCUMENT_TYPES.map((docType) => (
            <li
              key={docType.key}
              className={selectedType === docType.key ? styles["selected"] : ""}
              onClick={() => handleTypeSelect(docType.key)}
              style={{ cursor: "pointer" }}
              data-testid={`knowledge-doc-type-${docType.key}`}
            >
              {docType.label}
            </li>
          ))}
        </ul>
        <div className={styles["content"]}>
          <div className="flex justify-between items-center w-full font-sans">
            <div className="flex items-center gap-2">
              <h1 className={styles["headerTitle"]}>{selectedTypeLabel}</h1>
              <span className={styles["knowledgeStats"]}>
                有効: {allKnowledges.filter((k) => k.type === selectedType && k.isActive).length}/
                {MAX_ACTIVE_KNOWLEDGE_PER_TYPE}件
              </span>
              <span className={styles["knowledgeStatsTotal"]}>
                総数: {allKnowledges.filter((k) => k.type === selectedType).length}件
              </span>
            </div>
            {canManageKnowledge() && (
              <div className="flex-end flex gap-[4px] flex-col">
                <CommonButton
                  className="w-[92px]"
                  icon={<AddWhiteIcon />}
                  onClick={openModal}
                  data-testid="knowledge-add"
                >
                  新規追加
                </CommonButton>
              </div>
            )}
          </div>
          <div className={styles["searchBar"]}>
            <input
              type="text"
              placeholder="タイトルまたはプロンプトで検索"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className={styles["searchInput"]}
              data-testid="knowledge-search"
            />
          </div>
          <div>
            <PendingNotificationBanner />
            {isLoading ? (
              <div>読み込み中...</div>
            ) : knowledges.length > 0 ? (
              <KnowledgeTable
                knowledges={knowledges}
                allKnowledges={allKnowledges}
                onToggleActive={canManageKnowledge() ? handleToggleActive : undefined}
                onDelete={canManageKnowledge() ? handleDelete : undefined}
                onEdit={canManageKnowledge() ? handleEdit : undefined}
                searchQuery={searchQuery}
              />
            ) : (
              <div className={styles["emptyState"]}>
                <EmptyStateIcon />
                <p>
                  「ナレッジ」は、会話や議事録、用語集などを蓄積する知識ベースです。
                  <br />
                  保存した内容はAIの前提情報として活用され、回答や生成物の精度が向上します。
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        label={`ナレッジの新規追加`}
        isOpen={isModalOpen}
        onClose={closeModal}
        className="knowledgePendingModal"
        content={
          <CreateKnowledgeModal
            ref={createModalRef}
            onKnowledgeCreated={handleKnowledgeCreated}
            onClose={closeModal}
            selectedType={selectedType}
            onStateChange={setCreateFooterState}
            allKnowledges={allKnowledges}
            onToggleActive={handleToggleActive}
          />
        }
        footer={
          <div className={pendingStyles["closeButtonBottom"]}>
            <div />
            <div className={pendingStyles["footerActions"]}>
              <button
                className={pendingStyles["discardButton"]}
                onClick={() => createModalRef.current?.cancel()}
                disabled={createFooterState.isSubmitting}
              >
                キャンセル
              </button>
              <button
                className={pendingStyles["saveButtonCreate"]}
                onClick={() => createModalRef.current?.submit()}
                disabled={!createFooterState.canSubmit || createFooterState.isSubmitting}
                data-testid="knowledge-save"
              >
                {createFooterState.isSubmitting ? "作成中..." : "新規作成"}
              </button>
            </div>
          </div>
        }
      />
      <Modal
        label={`ナレッジの編集`}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        className="knowledgePendingModal"
        content={
          <CreateKnowledgeModal
            ref={editModalRef}
            onKnowledgeCreated={handleKnowledgeUpdated}
            onClose={closeEditModal}
            selectedType={selectedType}
            editingKnowledge={editingKnowledge}
            isEditMode={true}
            showActiveToggle={true}
            onStateChange={setEditFooterState}
            allKnowledges={allKnowledges}
            onToggleActive={handleToggleActive}
          />
        }
        footer={
          <div className={pendingStyles["closeButtonBottom"]}>
            <div>
              <button
                className={pendingStyles["closeButtonBottomBtn"]}
                onClick={() => editModalRef.current?.cancel()}
                disabled={editFooterState.isSubmitting}
                data-testid="knowledge-edit-close"
              >
                閉じる
              </button>
            </div>
            <div className={pendingStyles["footerActions"]}>
              <button
                className={pendingStyles["saveButton"]}
                onClick={() => editModalRef.current?.submit()}
                disabled={!editFooterState.canSubmit || editFooterState.isSubmitting}
              >
                {editFooterState.isSubmitting ? "更新中..." : "保存"}
              </button>
            </div>
          </div>
        }
      />
      <Modal
        label={`ナレッジ候補`}
        isOpen={isPendingModalOpen}
        onClose={() => setPendingModalOpen(false)}
        className="knowledgePendingModal"
        showCloseButton={true}
        content={
          <PendingKnowledgeModal
            onClose={() => setPendingModalOpen(false)}
            pendingKnowledges={pendingKnowledges}
            allKnowledges={allKnowledges}
            onApprove={canManageKnowledge() ? handleApprovePendingKnowledge : async () => {}}
            onDiscard={canManageKnowledge() ? handleDiscardPendingKnowledge : async () => {}}
            onToggleActive={canManageKnowledge() ? handleToggleActive : undefined}
            onRemoveFromPending={(knowledgeId) => {
              setPendingKnowledges((prev) => prev.filter((item) => item.id !== knowledgeId));
            }}
          />
        }
      />
    </div>
  );
};

export default Knowledges;
